// src/pages/billing/RevenueReportPage.tsx

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useBillingReport } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  ErrorAlert,
} from '@react/components';

function formatMoney(money: any): string {
  if (!money) return '--';
  const amount = (money.amount ?? 0) / Math.pow(10, money.decimal_places ?? 2);
  return amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export const RevenueReportPage = observer(() => {
  const store = useBillingReport();
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const today = now.toISOString().split('T')[0];

  const [periodStart, setPeriodStart] = useState(firstOfMonth);
  const [periodEnd, setPeriodEnd] = useState(today);
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await store.GetRevenueReport({
        period_start: new Date(periodStart).toISOString(),
        period_end: new Date(periodEnd).toISOString(),
      }) as any;
      setReport(res?.report ?? res);
    } catch {
      // Error handled by store
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <nav className="text-sm mb-2">
          <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
          <span className="mx-2 text-muted-foreground">/</span>
          <span className="text-foreground">Revenue Report</span>
        </nav>
        <h1 className="text-2xl font-bold text-foreground">Revenue Report</h1>
        <p className="text-sm text-muted-foreground mt-1">Revenue recognition and breakdown for the selected period</p>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-end">
            <Input label="Period Start" type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
            <Input label="Period End" type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
            <Button onClick={loadReport} disabled={loading}>
              {loading ? 'Generating...' : 'Generate Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {report && (
        <>
          {/* Revenue summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">Gross Revenue</p>
                <p className="text-2xl font-bold text-foreground">{formatMoney(report.gross_revenue)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">Discounts</p>
                <p className="text-2xl font-bold text-destructive">-{formatMoney(report.discounts)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">Refunds</p>
                <p className="text-2xl font-bold text-destructive">-{formatMoney(report.refunds)}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-5">
                <p className="text-sm text-muted-foreground">Net Revenue</p>
                <p className="text-2xl font-bold text-foreground">{formatMoney(report.net_revenue)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Deferred revenue */}
          <Card>
            <CardHeader>
              <CardTitle>Deferred Revenue</CardTitle>
              <CardDescription>Revenue recognition for the period</CardDescription>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-border">
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">Deferred Revenue (Start)</dt>
                  <dd className="text-sm text-foreground col-span-2">{formatMoney(report.deferred_revenue_start)}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">Recognized Revenue</dt>
                  <dd className="text-sm text-foreground col-span-2">{formatMoney(report.recognized_revenue)}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">New Deferred</dt>
                  <dd className="text-sm text-foreground col-span-2">{formatMoney(report.new_deferred)}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-muted-foreground">Deferred Revenue (End)</dt>
                  <dd className="text-sm text-foreground col-span-2 font-bold">{formatMoney(report.deferred_revenue_end)}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* By product */}
          {report.by_product?.items?.length > 0 && (
            <Card>
              <CardHeader><CardTitle>Revenue by Product</CardTitle></CardHeader>
              <CardContent>
                <Table striped>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.by_product.items.map((item: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{String(item.label ?? '--')}</TableCell>
                        <TableCell className="text-right">{formatMoney(item.amount)}</TableCell>
                        <TableCell className="text-right">{String(item.percentage ?? '--')}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* By period */}
          {report.by_period?.items?.length > 0 && (
            <Card>
              <CardHeader><CardTitle>Revenue by Period</CardTitle></CardHeader>
              <CardContent>
                <Table striped>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Period</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">%</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.by_period.items.map((item: any, i: number) => (
                      <TableRow key={i}>
                        <TableCell>{String(item.label ?? '--')}</TableCell>
                        <TableCell className="text-right">{formatMoney(item.amount)}</TableCell>
                        <TableCell className="text-right">{String(item.percentage ?? '--')}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
});

export default RevenueReportPage;
